import { z } from 'zod';
import alasql from 'alasql';
import type { OfficeToolDefinition } from './shared-tools';
import { fileStore } from '../services/file-store';

/**
 * Maximum rows returned from a single query to prevent token explosion.
 */
const MAX_RESULT_ROWS = 100;

export const queryAttachedFileTool: OfficeToolDefinition = {
  name: 'query_attached_file',
  description: `Run a SQL query against an attached file's data. The data is loaded into in-browser tables.

Each sheet of each attached file is available as a table. Table naming:
- Single-sheet files: use the file name without extension as the table name (spaces replaced with underscores).
- Multi-sheet files: use "fileName_sheetName" (spaces replaced with underscores).

Example queries:
- SELECT * FROM Sales_Data LIMIT 10
- SELECT Region, SUM(Revenue) as TotalRevenue FROM Sales_Data GROUP BY Region
- SELECT a.Name, b.Score FROM Sheet1 a JOIN Sheet2 b ON a.ID = b.ID

Use "SHOW TABLES" to list all available tables.
Returns up to ${MAX_RESULT_ROWS} rows per query.`,
  parameters: z.object({
    query: z
      .string()
      .describe('SQL query to execute against the attached file data.'),
  }),
  handler: async (params: any) => {
    const files = fileStore.getAll();

    if (files.length === 0) {
      return {
        error: 'No files are currently attached. Ask the user to attach a file first.',
      };
    }

    const query: string = params.query;

    // Handle "SHOW TABLES" pseudo-query
    if (query.trim().toUpperCase() === 'SHOW TABLES') {
      const tables: Array<{ table: string; rows: number; columns: number }> =
        [];
      for (const file of files) {
        for (const sheet of file.sheets) {
          tables.push({
            table: makeTableName(file.fileName, sheet.name, file.sheets.length > 1),
            rows: sheet.totalRows,
            columns: sheet.totalColumns,
          });
        }
      }
      return { tables };
    }

    // Register all file data as AlaSQL tables
    const registeredTables: string[] = [];
    try {
      for (const file of files) {
        for (const sheet of file.sheets) {
          const tableName = makeTableName(
            file.fileName,
            sheet.name,
            file.sheets.length > 1,
          );
          // Drop existing table if it exists, then create fresh
          try {
            alasql(`DROP TABLE IF EXISTS [${tableName}]`);
          } catch {
            // Ignore
          }
          alasql(`CREATE TABLE [${tableName}]`);
          alasql.tables[tableName].data = [...sheet.rows];
          registeredTables.push(tableName);
        }
      }

      // Execute the user query
      const result = alasql(query);

      // Limit result size
      if (Array.isArray(result)) {
        const truncated = result.length > MAX_RESULT_ROWS;
        return {
          data: result.slice(0, MAX_RESULT_ROWS),
          rowCount: result.length,
          truncated,
          ...(truncated && {
            note: `Result truncated to ${MAX_RESULT_ROWS} rows. Use LIMIT or more specific WHERE clauses to narrow results.`,
          }),
        };
      }

      return { result };
    } catch (err: any) {
      return {
        error: `SQL query failed: ${err.message}`,
        hint: 'Use "SHOW TABLES" to see available table names and check your query syntax.',
      };
    } finally {
      // Clean up registered tables
      for (const name of registeredTables) {
        try {
          alasql(`DROP TABLE IF EXISTS [${name}]`);
        } catch {
          // Ignore cleanup errors
        }
      }
    }
  },
};

/**
 * Build a SQL-safe table name from the file and sheet name.
 */
function makeTableName(
  fileName: string,
  sheetName: string,
  multiSheet: boolean,
): string {
  const base = fileName.replace(/\.[^.]+$/, ''); // remove extension
  const name = multiSheet ? `${base}_${sheetName}` : base;
  // Replace non-alphanumeric chars with underscores
  return name.replace(/[^a-zA-Z0-9_]/g, '_');
}

export const fileTools: OfficeToolDefinition[] = [queryAttachedFileTool];
